import moment from 'moment-timezone'
import rfdc from 'rfdc'
import getUuidByString from 'uuid-by-string'
import {
  BACKOFFICE_WEBSITES,
  DATE_FORMAT_CONFIG,
  TIME_FORMAT_CONFIG,
  LOCALES_LANGAUGE,
  DATE_FORMATS,
  DATE_PICKER_DATE_FORMATS,
  SESSION_STORAGE_HIDE_GAMES_TABLE,
} from './constants'

export function roundToPrecision(value, precision) {
  const multiplier = Math.pow(10, precision || 0)
  return Math.round(value * multiplier) / multiplier
}

export function truncToPrecision(value, precision) {
  const multiplier = Math.pow(10, precision || 0)
  return Math.trunc(value * multiplier) / multiplier
}

export function toTimeFormat(sec_num) {
  const hours = Math.floor(sec_num / 3600)
  const minutes = Math.floor((sec_num - hours * 3600) / 60)
  const seconds = sec_num - hours * 3600 - minutes * 60

  const hoursStr = hours ? hours + ':' : ''
  const minutesStr = minutes + ':'
  const secondsStr = seconds < 10 ? '0' + seconds : seconds

  return hoursStr + minutesStr + secondsStr
}

export function formatTimeOutput(time) {
  // Pad single-digit hours, minutes, and seconds
  return time
    .replace(/^(\d):/, '0$1:') // Pad hours if needed (e.g., 2: -> 02:)
    .replace(/:(\d):/, ':0$1:') // Pad minutes if needed (e.g., :5: -> :05:)
    .replace(/:(\d)$/, ':0$1') // Pad seconds if needed (e.g., :5 -> :05)
}

export function camelizeKeysId(obj) {
  return Object.entries(obj).reduce(
    (acc, entry) => Object.assign(acc, { [entry[0].replace(/ID$/g, 'Id')]: entry[1] }),
    {},
  )
}

// Returns the difference in minutes, between otherTzOffset and current user timezone offset
export function getTimezoneDifference(otherTzOffset) {
  const userTzOffset = new Date().getTimezoneOffset()

  return otherTzOffset - userTzOffset
}

export function nowInTimezone(otherTzOffset, _date) {
  const date = _date ? new Date(_date) : new Date()
  const difference = getTimezoneDifference(otherTzOffset)

  date.setHours(date.getHours() - difference / 60)

  return date
}

// Return milliseconds since beginning of current day of Date
export function timeFromDate(_date) {
  const date = new Date(_date)
  const unixWithTimeOfDay = date.getTime()

  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  const unixWithoutTimeOfDay = date.getTime()

  return unixWithTimeOfDay - unixWithoutTimeOfDay
}

// Return Date resetted to start of day
export function dateToStartOfDay(_date) {
  const date = new Date(_date)

  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date
}

export function dateToStartOfDayUTC(date, timezone = '') {
  const startDate = moment(date).tz(timezone || moment.tz.guess(), true)
  return startDate
}

export function dateToEndOfDayUTC(date, timezone = '') {
  const endDate = moment(date).tz(timezone || moment.tz.guess(), true)
  return endDate
}

export function getStartIsoString(date) {
  return date.format('YYYY-MM-DDT00:00:00.000') + 'Z'
}

export function getEndIsoString(date) {
  return date.format('YYYY-MM-DDT23:59:59.999') + 'Z'
}

// Returns the next round hour in milliseconds
export function roundTimeUp(time) {
  const difference = 60 * 60 * 1000 - (time % (60 * 60 * 1000))
  return time + difference
}

export function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * 60000)
}

// Feature detection for HTMLMediaElement.captureStream()
// TODO: Check about Firefox and Safari's status periodically
export const isCaptureStreamSupported = (() =>
  document.createElement('video').captureStream !== undefined)()

export function trackIntercomEvent(eventName) {
  if (window.Intercom && window.Intercom.booted) {
    window.Intercom('trackEvent', eventName)

    setTimeout(() => {
      window.Intercom('update', new Date().toISOString())
    }, 1000)
  }
}

export function handleVideoPlayInterrupt(error) {
  if (
    error.message.startsWith('The play() request was interrupted by a call to pause()') ||
    error.message.startsWith('The play() request was interrupted by a new load request')
  ) {
    return
  }

  throw error
}

export function handleAbortError(error) {
  if (error.name === 'AbortError') {
    return
  }

  throw error
}

export function createGuid() {
  let dt = new Date().getTime()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export function createUniqueNumberId(excludeIds = []) {
  while (true) {
    const randomId = parseFloat(Math.random().toFixed(8))

    if (!excludeIds.includes(randomId)) {
      return randomId
    }
  }
}

// Gets total required delay and an action's start time, calculates the remaining delay
// (if there is) and returns a promise which resolves when needed
export function waitAfterActionIfNeeded() {
  const MINIMUM_DELAY_FOR_SHOWING_MESSAGE_AFTER_ACTIONS = 1250
  const actionStartTime = Date.now()

  return (result) => {
    const actionTotalTime = Date.now() - actionStartTime

    const remainingDelay =
      actionTotalTime < MINIMUM_DELAY_FOR_SHOWING_MESSAGE_AFTER_ACTIONS
        ? MINIMUM_DELAY_FOR_SHOWING_MESSAGE_AFTER_ACTIONS - actionTotalTime
        : 0

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(result)
      }, remainingDelay)
    })
  }
}

export function unloadMediaElement(mediaElement) {
  mediaElement.pause()

  // Cross-browser right way to unload a media element
  // https://stackoverflow.com/questions/3258587/how-to-properly-unload-destroy-a-video-element/28060352
  mediaElement.removeAttribute('src')
  mediaElement.load()
}

export function boundInOutVideoPositions(inPosition, outPosition, duration) {
  const boundedInPosition = Math.clamp(inPosition, 0.5, duration - 1)
  const boundedOutPosition = Math.clamp(Math.max(outPosition, inPosition + 0.5), 1, duration - 0.5)

  return {
    inPosition: boundedInPosition,
    outPosition: boundedOutPosition,
  }
}

export function createDataUrlFrame(videoElement) {
  const canvas = document.createElement('canvas')
  canvas.width = 100
  canvas.height = 56

  const ctx = canvas.getContext('2d')

  ctx.drawImage(videoElement, 0, 0, 100, 56)

  return canvas.toDataURL('image/jpeg')
}

export function isBackofficeWebsite(website) {
  return BACKOFFICE_WEBSITES.indexOf(website) !== -1
}

export function shallowCompareObjects(object1, object2) {
  return (
    Object.keys(object1).length === Object.keys(object2).length &&
    Object.keys(object1).every(
      (key) => object2.hasOwnProperty(key) && object1[key] === object2[key],
    )
  )
}

export function checkIsImageURL(url) {
  return /\.(jpeg|jpg|gif|png)$/.test(url)
}

export function addPrecedingZero(num) {
  num = num.toString()
  return num >= 10 ? num : '0' + num
}

/**
 * //requires this.addPrecedingZero
 * Returnig readble date from unix time stamp: dd/mm/YYYY
 *
 * @param unixTime {Int16Array}
 * @param utc {Boolean}
 * @param timeOfDay {Boolean} add time of day to the returned string
 * @param monthsBefore {Boolean} choose: 3/15/2015 or 15/3/2015
 * @param lastTwoDigitsOfYear {Boolean} 2017 or 17
 * @returns {string} date string
 */

export function unixToDate(unixTime, utc, timeOfDay, monthsBefore, lastTwoDigitsOfYear = false) {
  var date = new Date(unixTime * 1000)

  var minutes = utc ? date.getUTCMinutes() : date.getMinutes()
  var hours = utc ? date.getUTCHours() : date.getHours()
  var dayOfMonth = utc ? date.getUTCDate() : date.getDate()
  var month = utc ? date.getUTCMonth() + 1 : date.getMonth() + 1
  var year = utc ? date.getUTCFullYear() : date.getFullYear()
  year = lastTwoDigitsOfYear ? year.toString().slice(2) : year

  var dateStr = monthsBefore
    ? month + '/' + dayOfMonth + '/' + year
    : dayOfMonth + '/' + month + '/' + year

  return timeOfDay
    ? dateStr + ' ' + addPrecedingZero(hours) + ':' + addPrecedingZero(minutes)
    : dateStr
}

export function getIntlDateFormat(dateDisplayFormat) {
  const dateFormatsLocalesMapping = {
    [DATE_FORMATS.MDY]: LOCALES_LANGAUGE.EN_US,
    [DATE_FORMATS.DMY]: LOCALES_LANGAUGE.EN_GB,
    [DATE_FORMATS.YMD]: LOCALES_LANGAUGE.EN_ZA,
  }
  const locale = dateFormatsLocalesMapping[dateDisplayFormat] ?? LOCALES_LANGAUGE.EN_US

  const { YEAR: year, MONTH: month, DAY: day } = DATE_FORMAT_CONFIG

  return new Intl.DateTimeFormat(locale, { year, month, day })
}

export function getIntlTimeFormat() {
  const { HOUR: hour, MINUTE: minute } = TIME_FORMAT_CONFIG

  return new Intl.DateTimeFormat(LOCALES_LANGAUGE.EN_GB, { hour, minute })
}

export function formatUnixTimeToDateString({ unixTime, intlDateFormat, formatToUtc = false }) {
  const date = getDateFromUnixTime(unixTime, formatToUtc)

  return intlDateFormat.format(date)
}

export function formatUnixTimeToFullDateTimeString({
  unixTime,
  intlDateFormat,
  intlTimeFormat,
  formatToUtc = false,
}) {
  const date = getDateFromUnixTime(unixTime, formatToUtc)
  const dateFormatted = intlDateFormat.format(date)
  const timeFormatted = intlTimeFormat.format(date)

  return `${dateFormatted} ${timeFormatted}`
}

function getDateFromUnixTime(unixTime, formatToUtc = false) {
  let date = new Date(unixTime * 1000)
  if (formatToUtc) {
    date = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
    )
  }
  return date
}

export function getDatePickerDateFormat(dateDisplayFormat) {
  const datePickerDateFormatsMapping = {
    [DATE_FORMATS.MDY]: DATE_PICKER_DATE_FORMATS.MDY,
    [DATE_FORMATS.DMY]: DATE_PICKER_DATE_FORMATS.DMY,
    [DATE_FORMATS.YMD]: DATE_PICKER_DATE_FORMATS.YMD,
  }
  return datePickerDateFormatsMapping[dateDisplayFormat] ?? DATE_PICKER_DATE_FORMATS.MDY
}

export const deepClone = rfdc()

export function isEmptyObject(obj) {
  return Boolean(obj && Object.keys(obj).length === 0 && obj.constructor === Object)
}

export function isDeepEmptyObject(obj) {
  return isEmptyObject(obj) || (obj && Object.values(obj).every((value) => !Boolean(value)))
}

export const getFileUuid = async (file) =>
  new Promise((resolve) => {
    const fr = new FileReader()
    fr.onload = () => {
      resolve(getUuidByString(fr.result))
    }
    fr.readAsDataURL(file)
  })

export function getUTCDate(date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
  )
}

export function hideGamesTable() {
  window.sessionStorage.setItem(SESSION_STORAGE_HIDE_GAMES_TABLE, true)
}

export function numberBetweenMinMax(num, min, max) {
  if (typeof max !== 'undefined' && !isNaN(max) && num > max) {
    return max
  }
  if (typeof min !== 'undefined' && !isNaN(min) && num < min) {
    return min
  }
  return num
}

export function secondsToTimeString(
  time,
  includeHours = false,
  splitter = ':',
  removePrecedingZeroFromFirst = false,
  pointsAfterDotOfSeconds = 0,
) {
  const timeInSeconds = Math.floor(time)
  let minutes = Math.floor(time / 60)
  let hours = Math.floor(minutes / 60)

  let seconds = Math.floor(timeInSeconds % 60)

  minutes = Math.floor(minutes % 60)

  hours = removePrecedingZeroFromFirst ? hours : addPrecedingZero(hours)
  minutes = !includeHours
    ? removePrecedingZeroFromFirst
      ? minutes
      : addPrecedingZero(minutes)
    : addPrecedingZero(minutes)
  seconds = addPrecedingZero(seconds)

  const timeArr = [minutes, seconds]

  if (includeHours) {
    timeArr.unshift(hours)
  }

  let timeFormatted = timeArr.join(splitter)

  const miliSecs = Math.floor((time - timeInSeconds) * 10 ** pointsAfterDotOfSeconds)

  if (miliSecs) {
    timeFormatted += `.${miliSecs}`
  }

  return timeFormatted
}

export function timeStringToSeconds(string, splitter = ':') {
  return string?.split(splitter).reduceRight((p, c, i, a) => {
    return p + c * Math.pow(60, a.length - i - 1)
  }, 0)
}

export const getFileExtension = (fileName) =>
  fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) ?? ''

export function formatFileSize(sizeInBytes) {
  if (sizeInBytes === 0) return '0 Bytes'

  const k = 1000
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(sizeInBytes) / Math.log(k))

  return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function checkIsObject(value) {
  // Check if the value is an object and not null
  const isObject = value && typeof value === 'object' && !Array.isArray(value)

  return isObject
}

export function checkIsObjectEmpty(value) {
  const isObject = checkIsObject(value)

  // If it's an object, check if it has no own properties
  return isObject && Object.keys(value).length === 0
}

export function removeLastOccurrence(str, strToRemove) {
  const lastIndex = str.lastIndexOf(strToRemove)
  if (lastIndex === -1) return str // Substring not found, return original string

  return str.slice(0, lastIndex) + str.slice(lastIndex + strToRemove.length)
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function getSentenceCaseString(string) {
  return string
    .split(' ')
    .map((item, index) => (index ? item.charAt(0).toLowerCase() + item.slice(1) : item))
    .join(' ')
}
